import { useContext, useEffect, useState } from 'react';
import { JamPadEvent } from '../DataModels';
import { supabase } from '../supabaseClient.js'

import { Book, Bookmark, BookmarkBorder, Favorite, FavoriteBorder, PersonAddAlt1, PersonRemoveAlt1 } from '@mui/icons-material';
import { Button, Typography } from '@mui/material';
import { EventMapContext } from '../App/App.tsx';

interface Props { event: JamPadEvent | undefined }
export const EventActions = ({ event } : Props) => {
    const defaultEventLength = 60 * 60 * 1000 * 3; // 3 hours in ms
    const { events, setEvents } = useContext(EventMapContext);
    const [loggedInID, setLoggedInID] = useState<string | undefined>(undefined);

    useEffect(() => {
        supabase.auth.getSession().then((res) => {
            setLoggedInID(res.data.session?.user.id);
        });
    }, []);

    async function doRsvpAction() {
        if(loggedInID && event) {
            if(!event?.rsvped) {
                const { error: rsvpErr } = await supabase
                    .from("event_rsvps")
                    .insert({ event_id: event.id });
                if(rsvpErr) {
                    console.error("Failed to RSVP event: " + rsvpErr.message);
                } else {
                    event.rsvped = true;
                    event.rsvps++;
                    const newEvents = new Map(events);
                    newEvents.set(event.id, event);
                    setEvents(newEvents);
                }
            } else if(window.confirm("Un-RSVP from this event?")) {
                const { error: unrsvpErr } = await supabase
                    .from("event_rsvps")
                    .delete()
                    .eq("profile_id", loggedInID)
                    .eq("event_id", event?.id);
                if(unrsvpErr) {
                    console.error("Failed to un-RSVP event: " + unrsvpErr.message);
                } else {
                    event.rsvped = false;
                    event.rsvps--;
                    const newEvents = new Map(events);
                    newEvents.set(event.id, event);
                    setEvents(newEvents);
                }
            }
        } else {
            alert("You must be logged in to RSVP events.");
        }
    }

    async function doLikeAction() {
        if(loggedInID && event) {
            if(!event?.liked) {
                const { error: likeErr } = await supabase
                    .from("event_likes")
                    .insert({ event_id : event.id });
                if(likeErr) {
                    console.error("Failed to like event: " + likeErr.message);
                } else {
                    event.liked = true;
                    event.likes++;
                    const newEvents = new Map(events);
                    newEvents.set(event.id, event);
                    setEvents(newEvents);
                }
            } else {
                const { error: unlikeErr } = await supabase
                    .from("event_likes")
                    .delete()
                    .eq("profile_id", loggedInID)
                    .eq("event_id", event.id);
                if(unlikeErr) {
                    console.error("Failed to unlike event: " + unlikeErr.message);
                } else {
                    event.liked = false;
                    event.likes--;
                    const newEvents = new Map(events);
                    newEvents.set(event.id, event);
                    setEvents(newEvents);
                }
            }
        } else {
            alert("You must be logged in to like events.");
        }
    }

    async function doSaveAction() {
        if(loggedInID && event) {
            if(!event?.saved) {
                const { error: saveErr } = await supabase
                    .from("event_saves")
                    .insert({ event_id : event.id });
                if(saveErr) {
                    console.error("Failed to save event: " + saveErr.message);
                } else {
                    event.saved = true;
                    event.saves++;
                    const newEvents = new Map(events);
                    newEvents.set(event.id, event);
                    setEvents(newEvents);
                }
            } else {
                const { error: unsaveErr } = await supabase
                    .from("event_saves")
                    .delete()
                    .eq("profile_id", loggedInID)
                    .eq("event_id", event.id);
                if(unsaveErr) {
                    console.error("Failed to unsave event: " + unsaveErr.message);
                } else {
                    event.saved = false;
                    event.saves--;
                    const newEvents = new Map(events);
                    newEvents.set(event.id, event);
                    setEvents(newEvents);
                }
            }
        } else {
            alert("You must be logged in to like events.");
        }
    }



    return <div style={{ display: 'flex', flexDirection: 'row', gap: '6px',
            alignItems: 'center', justifyContent: 'center', flexWrap: 'nowrap' 
        }}>
            <div onClick={(e) => {
                e.stopPropagation();
                doLikeAction();
            }} style={{ 
                cursor: 'pointer', position: 'relative' 
            }}>
                { event?.liked ? 
                    <Favorite sx={{ color: 'red' }} fontSize='large'/>
                :
                    <FavoriteBorder sx={{ color: 'lightgray' }} fontSize='large'/>
                }
                <Typography variant='caption' style={{ color: 'white', 
                    position: 'absolute', top: '20%', left: '40%' 
                }}>{event?.likes}</Typography>
            </div>

            { Date.now() > new Date().setTime(event?.date.getTime() ?? 0 + defaultEventLength) ?
                <Button onClick={(e) => e.stopPropagation()}
                    variant='contained'
                    sx={{ fontSize: '12px', borderRadius: '6px', padding: '6px 8px', 
                        backgroundColor: 'gray', color: 'lightgray'
                    }}
                >Event Ended </Button>
            :
                <Button variant='contained' color={event?.rsvped ? 'info' : 'secondary'}
                    sx={{ display: 'flex', borderRadius: '6px', fontSize: '12px', 
                        gap: '6px', padding: '6px 8px', alignItems: 'start'
                    }}
                    onClick={(e) => {
                        e.stopPropagation();
                        doRsvpAction();
                    }}
                >
                    { event?.rsvped ? <PersonRemoveAlt1 fontSize='small'/> : <PersonAddAlt1 fontSize='small'/>}
                    <div>{ event?.rsvped ? "I'm going!" : "RSVP" }</div>
                </Button>
            }

            <div onClick={(e) => {
                e.stopPropagation();
                doSaveAction();
            }} style={{ 
                cursor: 'pointer', position: 'relative' 
            }}>
                { event?.saved ? 
                    <Bookmark sx={{ color: 'blue' }} fontSize='large'/>
                :
                    <BookmarkBorder sx={{ color: 'lightgray' }} fontSize='large'/>
                }
                <Typography variant='caption' style={{ color: 'white', 
                    position: 'absolute', top: '20%', left: '40%' 
                }}>{event?.saves}</Typography>
            </div>
        </div>;
};
export default EventActions;